// Pure payload-building + validation for the ITAM-2614 consult RFZO therapy
// (step 1, POST /rfzo_therapy/pharmacist_create). Extracted from React so the
// logic is unit-testable without rendering. Consumed by useConsultOrderLifecycle
// (submitRfzo) and by ConsultRfzoFormModal (field-linked errors).

import moment from "moment";
import { ConsultRfzoDraft } from "../contexts/ConsultOrderContext";
import { validateTherapySchedule } from "./rfzo/therapyScheduleValidation";
import {
  ConsultRfzoCreateRequest,
  ConsultRfzoItemRequest,
  ConsultRfzoTherapyItem,
} from "../types/consultRfzo.types";

/**
 * D9/D13: is this therapy line orderable? `quantity` is the step-1 stock
 * snapshot, `rfzoQuantity` the prescribed amount — step 2 filters on the SAME
 * snapshot, so the FE gate agrees with what the backend will do. A null on
 * either side means "not provably in stock" and counts as understocked, so the
 * per-line badge and the order gate can never contradict each other (both are
 * derived from this single predicate).
 */
export const isRfzoItemInStock = (item: ConsultRfzoTherapyItem): boolean =>
  item.rfzoQuantity != null &&
  item.quantity != null &&
  item.quantity >= item.rfzoQuantity;

/** True when at least one line is orderable — the "Kreiraj rezervaciju" gate. */
export const someRfzoItemInStock = (
  items: ConsultRfzoTherapyItem[] | null | undefined,
): boolean => (items ?? []).some(isRfzoItemInStock);

export interface ConsultRfzoMarketplaceInfo {
  marketplaceId: number;
  branchMarketplaceId: number;
}

/** "NORMAL" = regular create; "NO_CONTENT" = the explicit "korisnik nema terapiju" action. */
export type ConsultRfzoSubmitMode = "NORMAL" | "NO_CONTENT";

export interface ConsultRfzoValidationErrors {
  userEmail?: string;
  lbo?: string;
  jmbg?: string;
  firstName?: string;
  secondName?: string;
  items?: string;
  lastTherapyDate?: string;
}

export interface ConsultRfzoValidationResult {
  valid: boolean;
  errors: ConsultRfzoValidationErrors;
}

/**
 * Validate the RFZO draft for submit-time gating.
 *
 * The NO_CONTENT mode skips the items + date rules (the payload builder strips
 * them), but keeps the identity rules — the backend still resolves the user by
 * e-mail and requires an identifier for every outcome.
 *
 * ITAM-2716: the identifier rules mirror the server exactly, trim-before-test
 * included. This is the PRIMARY guard, not a convenience: the server answers an
 * identifier rejection with a bare Serbian sentence carrying no errorCode and no
 * field key, so nothing that reaches the server can be mapped back to a field.
 */
export const validateConsultRfzoDraft = (
  draft: ConsultRfzoDraft,
  userEmail: string | null | undefined,
  mode: ConsultRfzoSubmitMode = "NORMAL",
): ConsultRfzoValidationResult => {
  const errors: ConsultRfzoValidationErrors = {};

  if (!userEmail || !userEmail.trim()) {
    errors.userEmail =
      "Korisnik nema e-mail nalog u apoteka.rs — RFZO terapija ne može da se kreira.";
  }
  const lbo = draft.lbo.trim();
  const jmbg = draft.jmbg.trim();
  if (!lbo && !jmbg) {
    const message = "Unesite LBO ili JMBG korisnika.";
    errors.lbo = message;
    errors.jmbg = message;
  } else if (jmbg && !/^[0-9]{13}$/.test(jmbg)) {
    // Digit count and charset only: the server runs no checksum and no date
    // plausibility check, so a stricter rule here would reject records the
    // backend itself accepts.
    errors.jmbg = "JMBG mora imati tačno 13 cifara.";
  }
  // D11: these become the order's customer name; without them the backend
  // defaults the order to "RFZO porudžbina".
  if (!draft.firstName.trim()) {
    errors.firstName = "Ime je obavezno.";
  }
  if (!draft.secondName.trim()) {
    errors.secondName = "Prezime je obavezno.";
  }

  if (mode === "NORMAL") {
    if (draft.items.length < 1) {
      errors.items = "Dodajte bar jedan artikal.";
    } else {
      const badLine = draft.items.find(
        (line) =>
          !Number.isInteger(line.rfzoQuantity) ||
          line.rfzoQuantity < 1 ||
          !Number.isFinite(line.rfzoParticipationPrice) ||
          line.rfzoParticipationPrice < 0,
      );
      if (badLine) {
        errors.items = `Neispravna količina ili participacija za artikal "${badLine.name}".`;
      }
    }

    // D6 / D12: the schedule rules are shared with the order-processing RFZO
    // answer form (RFZOOrderTherapy); see validateTherapySchedule.
    const scheduleError = validateTherapySchedule(
      draft.willBeReady,
      draft.lastTherapyDate,
    );
    if (scheduleError) {
      errors.lastTherapyDate = scheduleError;
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

/**
 * Build the exact pharmacist_create body. The serialized identifier keys are
 * `lbo` and `jmbg` (D1: "LBO"/"JMBG" are silently dropped by the backend).
 * Items send itemId ONLY (never externalId, D5). Empty optional strings are
 * omitted. NO_CONTENT sends items: [] and omits BOTH dates and noteForUser: the
 * backend notification text branches on willBeReady, and a leftover date would
 * tell the patient the therapy "will be available", contradicting the
 * no-content message.
 */
export const buildConsultRfzoCreatePayload = (
  draft: ConsultRfzoDraft,
  userEmail: string,
  marketplaceInfo: ConsultRfzoMarketplaceInfo,
  mode: ConsultRfzoSubmitMode = "NORMAL",
): ConsultRfzoCreateRequest => {
  const payload: ConsultRfzoCreateRequest = {
    userEmail: userEmail.trim(),
    marketplaceId: marketplaceInfo.marketplaceId,
    branchMarketplaceId: marketplaceInfo.branchMarketplaceId,
    items: [],
  };

  // ITAM-2716: the server normalizes a blank identifier to null itself, so an
  // empty string would be harmless. Omitting keeps the body honest about what
  // the pharmacist actually entered.
  const lbo = draft.lbo.trim();
  if (lbo) {
    payload.lbo = lbo;
  }
  const jmbg = draft.jmbg.trim();
  if (jmbg) {
    payload.jmbg = jmbg;
  }

  const firstName = draft.firstName.trim();
  if (firstName) {
    payload.firstName = firstName;
  }
  const secondName = draft.secondName.trim();
  if (secondName) {
    payload.secondName = secondName;
  }
  const userPhone = draft.userPhone.trim();
  if (userPhone) {
    payload.userPhone = userPhone;
  }
  const noteForMarketplace = draft.noteForMarketplace.trim();
  if (noteForMarketplace) {
    payload.noteForMarketplace = noteForMarketplace;
  }

  if (mode === "NO_CONTENT") {
    return payload;
  }

  const items: ConsultRfzoItemRequest[] = draft.items.map((line) => ({
    itemId: line.itemId,
    rfzoQuantity: line.rfzoQuantity,
    rfzoParticipationPrice: line.rfzoParticipationPrice,
  }));
  payload.items = items;

  const noteForUser = draft.noteForUser.trim();
  if (noteForUser) {
    payload.noteForUser = noteForUser;
  }
  // lastTherapyDate only ever travels WITH willBeReady: it exists to bound the
  // reminder window (D12), and the backend's reminder generation is driven by
  // willBeReady. Sending it alone (a stray month click on a therapy that is
  // available now) would persist a horizon the pharmacist never intended.
  if (draft.willBeReady) {
    payload.willBeReady = moment(draft.willBeReady).format("YYYY-MM-DD");
    if (draft.lastTherapyDate) {
      payload.lastTherapyDate = moment(draft.lastTherapyDate).format(
        "YYYY-MM-DD",
      );
    }
  }

  return payload;
};
